import { history } from 'instantsearch.js/es/lib/routers';

const SPECIAL_LEGACY_KEYS = {
  'filter.ss_size_instock': 'size',
  'filter.ss_gender': 'gender',
  'filter.mfield_custom_category': 'category',
  'filter.mfield_custom_subcategory': 'category',
  // 'filter.mfield_custom_print_collection': 'print',
  'filter.mfield_custom_print_collection': 'print_collection',
  'filter.ss_age_group_instock': 'age_group',
  'filter.ss_available.low': 'available',
  'filter.ss_available.high': 'available',
  'filter.mfield_custom_sleeve_length': 'sleeve_length',
  'filter.mfield_my_fields_color': 'color',
};

function getRefinementValueFromRoute(value) {
  if (!value) return null;
  if (Array.isArray(value)) return value.map(decodeURIComponent);
  if (value?.includes(',')) return value.split(',').map(decodeURIComponent);

  return decodeURIComponent(value);
}

function encodeRefinementValue(value) {
  if (Array.isArray(value)) {
    return value?.map(encodeURIComponent);
  }
  return encodeURIComponent(value);
}

export function getCurrentUrlParams(location, useObject = true) {
  const [baseUrl, searchParams] = location.href.split('?');
  const queryParams = new URLSearchParams(searchParams);

  // Remove from queryParams the query params that are in ALGOLIA_QUERY_PARAMS (even if they are array): ie: query, page, color, size, color[N], size[N]... based on its type
  Object.entries(Object.fromEntries(queryParams)).forEach(([key]) => {
    const name = key.replace(/\[\d+\]/, '');
    if (name !== key || ['page', 'query', 'price'].includes(name)) {
      queryParams.delete(key);
    }
  });

  if (!useObject) {
    // Do not transform the query params into object/array and return the URLSearchParams object
    return queryParams;
  }

  return { baseUrl, queryParams: Object.fromEntries(queryParams) };
}

function clearEmptyQueryParams(params) {
  let newObj = {};
  Object.keys(params).forEach((key) => {
    if (
      !(
        params[key] === undefined ||
        params[key] === '' ||
        params[key] === 'undefined'
      )
    ) {
      newObj[key] = params[key];
    }
  });
  return newObj;
}

/** Shorten facet attribute to URL key (must match stateToRoute) */
function facetToShortKey(attribute) {
  return attribute.replace(/^(options|meta\.custom|inventory)(\.|_)/, '');
}

/**
 * Create routing config that maps URL params to the correct refinementList
 * attribute names. Pass the section's facet list so routeToState can resolve
 * e.g. URL "color" -> "options.color" when that's the actual facet attribute,
 * ensuring configure (collection filter) and route state facets apply in one request.
 *
 * @param {string[]} [facets] - Index attribute names, e.g. ['options.color', 'meta.custom.size']
 */
export function createRouting(facets = []) {
  const shortKeyToAttribute = new Map();
  facets.forEach((attr) => {
    const short = facetToShortKey(attr);
    if (!shortKeyToAttribute.has(short)) {
      shortKeyToAttribute.set(short, attr);
    }
  });

  return {
    router: history({
      cleanUrlOnDispose: false,

      createURL({ qsModule, routeState, location }) {
        const { baseUrl, queryParams } = getCurrentUrlParams(location);

        Object.entries(routeState).forEach(([key, value]) => {
          queryParams[key] = encodeRefinementValue(value);
        });

        const queryString = qsModule.stringify(
          clearEmptyQueryParams(queryParams),
          {
            addQueryPrefix: true,
            arrayFormat: 'indices',
          }
        );

        return [baseUrl, queryString].join('');
      },

      parseURL({ qsModule, location }) {
        let queryParams = {};

        try {
          const searchParams = qsModule.parse(location.search.slice(1));
          const { query = '', page = 1, price, ...otherParams } = searchParams;
          const matches = window.location.pathname.match(
            /\/collections\/([^/]+)/i
          );
          const collectionHandle =
            matches && matches.length === 2 ? matches[1] : null;

          if (collectionHandle) {
            queryParams.collections = [collectionHandle];
          }

          Object.entries(otherParams).forEach(([key, value]) => {
            // We should ignore UTMs or other tracking parameters
            if (
              key.match(
                /^utm_|^nb_|^nbt|^h_ad_|^fbc|^fb_|^gclid|^gclsrc|^dclid|^ad|^campaign/
              )
            ) {
              return;
            }

            let parsedKey = SPECIAL_LEGACY_KEYS[key] || key;
            const refinementValue = getRefinementValueFromRoute(value);

            if (refinementValue) {
              queryParams[parsedKey] = refinementValue;
            }
          });

          return {
            query: decodeURIComponent(query),
            page,
            price: price !== undefined ? decodeURIComponent(price) : undefined,
            ...queryParams,
          };
        } catch (error) {
          console.error('Error parsing URL', error);
        }

        return queryParams;
      },
    }),

    stateMapping: {
      stateToRoute(uiState) {
        const indexUiState = uiState[window.ALGOLIA_INDEX_NAME] || {};
        const refinementList = Object.entries({
          ...(indexUiState.refinementList || {}),
          ...(indexUiState.range || {}),
        }).reduce((acc, [key, value]) => {
          const idx = key.replace(
            /^(options|meta\.custom|inventory)(\.|_)/,
            ''
          );
          acc[idx] = value;
          return acc;
        }, {});

        return {
          query: indexUiState.query,
          page: indexUiState.page,
          ...refinementList,
        };
      },

      routeToState(routeState) {
        const { query, page, price, collections, ...list } = routeState;

        const refinementList = Object.entries(list).reduce(
          (acc, [key, value]) => {
            if (
              ['query', 'page', 'price', 'collections'].includes(key) ||
              value === undefined ||
              value === ''
            ) {
              return acc;
            }
            // Map URL key to the actual index attribute so widgets apply the refinement
            const attribute =
              shortKeyToAttribute.get(key) ||
              (key === 'available'
                ? 'inventory_available'
                : `meta.custom.${key}`);
            acc[attribute] = value;
            return acc;
          },
          {}
        );

        return {
          [window.ALGOLIA_INDEX_NAME]: {
            query: query,
            page: page,
            range: price !== undefined ? { price } : {},
            refinementList,
          },
        };
      },
    },
  };
}

/** @deprecated Use createRouting(facets) so route state maps to correct facet attributes */
export const routing = createRouting();
